<script setup>
import { ref, computed, nextTick } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '@/components/icon/IconCross.vue'
import { SOUNDS_LIKE_MAX } from './captionRecognition'

const props = defineProps({
	modelValue: { type: Array, default: () => [] },
	// The term these pronunciations belong to, for labels.
	term: { type: String, default: '' },
	id: { type: String, default: undefined },
	invalid: { type: Boolean, default: false },
	describedBy: { type: String, default: undefined },
	disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'enter-empty'])

const text = ref('')
const input = ref(null)

const chips = computed(() => props.modelValue)
const full = computed(() => chips.value.length >= SOUNDS_LIKE_MAX)
const termName = computed(() => props.term.trim() || 'this term')

/** Adds each comma-separated piece of `raw` until the cap; returns what didn't fit. */
function commit(raw) {
	const next = [...chips.value]
	const pieces = String(raw).split(',').map((s) => s.trim()).filter(Boolean)
	const rest = []
	for (const piece of pieces) {
		if (next.some((c) => c.toLowerCase() === piece.toLowerCase())) continue
		if (next.length >= SOUNDS_LIKE_MAX) {
			rest.push(piece)
			continue
		}
		next.push(piece)
	}
	if (next.length !== chips.value.length) emit('update:modelValue', next)
	return rest.join(', ')
}

function onKeydown(e) {
	if (e.key === 'Enter' || e.key === ',') {
		e.preventDefault()
		if (!text.value.trim()) {
			if (e.key === 'Enter') emit('enter-empty')
			return
		}
		text.value = commit(text.value)
	} else if (e.key === 'Backspace' && !text.value && chips.value.length) {
		e.preventDefault()
		emit('update:modelValue', chips.value.slice(0, -1))
	}
}

function onPaste(e) {
	const pasted = e.clipboardData?.getData('text') || ''
	if (!pasted.includes(',')) return
	e.preventDefault()
	text.value = commit(`${text.value}${pasted}`)
}

function onBlur() {
	if (text.value.trim()) text.value = commit(text.value)
}

async function remove(index) {
	const next = chips.value.filter((_, i) => i !== index)
	emit('update:modelValue', next)
	await nextTick()
	input.value?.focus()
}

defineExpose({ focus: () => input.value?.focus() })
</script>

<template>
	<div
		class="flex min-h-8 w-full flex-wrap items-center gap-1 rounded bg-surface-3 px-1.5 py-1 transition-colors focus-within:bg-surface-4"
		:class="invalid ? 'ring-1 ring-error-light' : ''"
		data-testid="sounds-like"
		@click.self="input?.focus()"
	>
		<span
			v-for="(chip, i) in chips"
			:key="chip"
			class="inline-flex h-6 max-w-full items-center gap-1 rounded-full bg-surface-5/70 pl-2.5 pr-0.5 text-xs text-surface-text-2"
			data-testid="sounds-like-chip"
		>
			<span class="truncate">{{ chip }}</span>
			<button
				type="button"
				class="flex size-5 shrink-0 items-center justify-center rounded-full text-surface-7 transition-colors hover:bg-surface-6 hover:text-white disabled:opacity-40"
				:aria-label="`Remove “${chip}” from ${termName}`"
				:disabled="disabled"
				@click="remove(i)"
			>
				<icon-base width="8" height="8"><icon-cross /></icon-base>
			</button>
		</span>
		<input
			v-show="!full"
			:id="id"
			ref="input"
			v-model="text"
			type="text"
			class="h-6 min-w-[6rem] flex-1 border-0 bg-transparent px-1 text-base text-surface-text-2 placeholder:text-tiny placeholder:text-surface-7 focus:outline-none focus:ring-0 md:text-tiny"
			:placeholder="chips.length ? 'Add another' : 'Optional'"
			:aria-label="`Sounds like, for ${termName}. Press Enter or comma to add. Up to ${SOUNDS_LIKE_MAX}.`"
			:aria-invalid="invalid ? 'true' : undefined"
			:aria-describedby="describedBy"
			:disabled="disabled"
			autocomplete="off"
			enterkeyhint="done"
			@keydown="onKeydown"
			@paste="onPaste"
			@blur="onBlur"
		>
		<span v-if="full" class="px-1 text-[11px] text-surface-7" data-testid="sounds-like-full">{{ SOUNDS_LIKE_MAX }} max</span>
	</div>
</template>
